<script lang="ts">
    import type { Secret } from '../lib/types';
    import { formatRelative, expirationStatus, formatRelativeLocale, usageStatus } from '../lib/format';
    import { api } from '../lib/api';
    import { toast } from '../lib/stores/toast.svelte';
    import { t, i18n } from '../lib/i18n.svelte';

    let { secret, onEdit, onDelete }: {
        secret: Secret;
        onEdit: () => void;
        onDelete: () => void;
    } = $props();

    const exp = $derived(expirationStatus(secret.expiresAt));
    const usage = $derived(usageStatus(secret));

    let revealed = $state(false);
    let value = $state<string | null>(null);
    let loading = $state(false);

    async function fetchValue(): Promise<string | null> {
        if (value !== null) return value;
        loading = true;
        try {
            const res = await api<{ value: string }>(`/api/secrets/${secret.id}/reveal`);
            value = res.value;
            return res.value;
        } catch (err: any) {
            toast.error(`Failed to reveal: ${err.message}`);
            return null;
        } finally {
            loading = false;
        }
    }

    async function toggleReveal(e: MouseEvent) {
        e.stopPropagation();
        if (revealed) {
            revealed = false;
            // Wipe from memory after hiding for safety
            value = null;
            return;
        }
        const v = await fetchValue();
        if (v !== null) revealed = true;
    }

    async function copyValue(e: MouseEvent) {
        e.stopPropagation();
        const v = await fetchValue();
        if (v === null) return;
        try {
            await navigator.clipboard.writeText(v);
            toast.success(`Copied ${secret.keyName} to clipboard`);
        } catch {
            toast.error('Clipboard access denied');
        }
    }
</script>

<div class="row">
    <div class="drag-handle" title="Drag to move">⋮⋮</div>
    <div class="info">
        <span class="key mono">{secret.keyName}</span>
        {#if revealed && value !== null}
            <span class="value mono" title="Click eye icon to hide">{value}</span>
        {:else}
            <span class="masked mono">••••••••</span>
        {/if}
    </div>
    <div class="meta">
        <span class="badge info">v{secret.version}</span>
        {#if exp.level !== 'ok'}
      <span class="badge {exp.level === 'expired' ? 'danger' : exp.level === 'danger' ? 'danger' : 'warning'}">
        ⏰ {exp.label}
      </span>
        {/if}
        <span
            class="dim usage-cell"
            class:usage-grey={usage.level === 'never' || usage.level === 'never-pretracking'}
            class:usage-amber={usage.level === 'stale'}
            title={t('usage.createdTooltip', {
                en: `Created ${formatRelativeLocale(secret.createdAt, 'en')}`,
                nb: `Opprettet ${formatRelativeLocale(secret.createdAt, 'nb')}`,
            })}
        >
            {#if secret.lastFetchedAt}
                ⬇ {formatRelativeLocale(secret.lastFetchedAt, i18n.locale)}
                · {secret.fetchedCount}×
            {:else}
                ⬇ {t('usage.neverShort', { en: 'never', nb: 'aldri' })}
            {/if}
        </span>
        <span class="dim">{formatRelative(secret.updatedAt)}</span>
        <button
                class="ghost no-drag icon-btn"
                onclick={toggleReveal}
                disabled={loading}
                title={revealed ? 'Hide value' : 'Show value'}
                aria-label={revealed ? 'Hide value' : 'Show value'}
        >
            {revealed ? '🙈' : '👁'}
        </button>
        <button
                class="ghost no-drag icon-btn"
                onclick={copyValue}
                disabled={loading}
                title="Copy value to clipboard"
                aria-label="Copy value to clipboard"
        >
            📋
        </button>
        <button class="ghost no-drag" onclick={(e) => { e.stopPropagation(); onEdit(); }}>Edit</button>
        <button class="ghost no-drag" onclick={(e) => { e.stopPropagation(); onDelete(); }}>🗑</button>
    </div>
</div>

<style>
    .row {
        display: flex; align-items: center; justify-content: space-between;
        padding: 8px 12px; border-radius: var(--radius);
        border: 1px solid transparent;
        user-select: none;
        gap: 12px;
    }
    .row:hover { background: var(--bg-2); border-color: var(--border); }
    .drag-handle {
        color: var(--text-3);
        font-size: 14px;
        padding: 0 6px 0 0;
        flex-shrink: 0;
    }
    .info {
        display: flex; align-items: center; gap: 14px;
        min-width: 0; flex: 1;
        overflow: hidden;
    }
    .key { font-weight: 600; flex-shrink: 0; }
    .masked { color: var(--text-3); letter-spacing: 2px; }
    .value {
        color: var(--warning);
        background: rgba(245, 158, 11, 0.1);
        padding: 2px 8px;
        border-radius: 4px;
        font-size: 12px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 400px;
        user-select: all; /* let users select-copy the visible text too */
    }
    .meta {
        display: flex; align-items: center; gap: 8px;
        font-size: 12px;
        flex-shrink: 0;
    }
    .usage-cell { font-variant-numeric: tabular-nums; }
    .usage-grey { color: var(--text-3); opacity: 0.7; }
    .usage-amber { color: var(--warning, #f59e0b); font-weight: 500; }
    .icon-btn {
        padding: 4px 8px;
        font-size: 14px;
        line-height: 1;
    }
</style>